import { useState, useEffect, useCallback, useRef } from "react"
import { Link, useLocation, useHistory } from "react-router-dom"
import {
  Navbar,
  Nav,
  Dropdown,
  NavDropdown,
  Offcanvas,
} from "react-bootstrap"

import "react-bootstrap-typeahead/css/Typeahead.css"
import "./Navbar.css"
import OrganizationBox from "../Settings/OrganizationBox"
import Conversations from "../Chat/Conversations"
import LanguageToggle from "./LanguageToggle"

import API from "../../common/API"
import { Routes } from "../../routes"
import { useTranslation } from "react-i18next"
import settings from "../../common/Settings"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"
import { createRealtimeConnection, startConnectionSafe, stopConnectionSafe } from "@/services/realtime"
import { useToast } from "./Toast"

export default function ResponsiveNavbar(): JSX.Element {
  const { t } = useTranslation("global");
  const { info } = useToast()
  const user: any = parsedUser();
  const location = useLocation()
  const history = useHistory()
  const [expanded, setExpanded] = useState(false)

  const [chatCount, setChatCount] = useState(0)
  const [favoritesCount, setFavoritesCount] = useState(0)
  const [showChat, setShowChat] = useState(false)
  const navRef = useRef<HTMLDivElement | null>(null)
  const prevChatCountRef = useRef<number>(0)
  const unreadInitializedRef = useRef<boolean>(false)

  const toggleSearch = useCallback(() => {
    history.push("/search")
  }, [history])

  const toggleFavorites = useCallback(() => {
    history.push(Routes.Favorites.path)
    setExpanded(false)
  }, [history])
  const handleToggle = useCallback(() => {
    setExpanded(prev => !prev)
  }, [])

  // Close navbar when clicking on nav items
  const handleNavClick = useCallback(() => {
    setExpanded(false)
  }, [])

  // Close navbar on outside click or escape key
  useEffect(() => {
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && expanded) {
        handleNavClick()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (!expanded) return
      const target = e.target as Node
      if (navRef.current && !navRef.current.contains(target)) {
        handleNavClick()
      }
    }

    document.addEventListener("keydown", handleEscape)
    document.addEventListener("click", handleClickOutside)

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.removeEventListener("click", handleClickOutside)
    }
  }, [expanded, handleNavClick])

  // Prevent background scroll when navbar is expanded on mobile
  useEffect(() => {
    if (expanded) document.body.classList.add("navbar-expanded")
    else document.body.classList.remove("navbar-expanded")

    return () => {
      document.body.classList.remove("navbar-expanded")
    }
  }, [expanded])

  const toggleChat = useCallback(() => {
    setShowChat(prev => !prev)
  }, [])

  const requestChatCount = useCallback(async () => {
    if (!user?.ID) return
    try {
      const response = await API.getAction(
        "Chat/UnreadCount",
        "userId=" + user.ID
      )
      if (response.data.status === "ok") {
        const unreadCount = Number(response.data.response) || 0
        setChatCount(unreadCount)
      }
    } catch (error) {
      console.error("Error fetching chat count:", error)
    }
  }, [user.ID])

  const requestFavoritesCount = useCallback(async () => {
    if (!user?.ID) return
    try {
      const response = await API.getAction("place/priorityList", `accountId=${user.ID}`)
      if (response.data.status === "ok") {
        const count = Array.isArray(response.data.response) ? response.data.response.length : 0
        setFavoritesCount(count)
      }
    } catch (error) {
      console.error("Error fetching favorites count:", error)
    }
  }, [user.ID])

  const refreshCounters = useCallback(() => {
    requestChatCount()
    requestFavoritesCount()
  }, [requestChatCount, requestFavoritesCount])

  useEffect(() => {
    refreshCounters()
    const interval = setInterval(refreshCounters, 30000)
    return () => clearInterval(interval)
  }, [refreshCounters])

  useEffect(() => {
    if (!unreadInitializedRef.current) {
      unreadInitializedRef.current = true
      prevChatCountRef.current = chatCount
      return
    }

    if (chatCount > prevChatCountRef.current) {
      const delta = chatCount - prevChatCountRef.current
      const title = t("Chats")
      const message =
        delta === 1
          ? "Tienes 1 mensaje nuevo sin leer."
          : `Tienes ${delta} mensajes nuevos sin leer.`
      info(title, message, 4500)
    }

    prevChatCountRef.current = chatCount
  }, [chatCount, info, t])

  useEffect(() => {
    if (!user?.ID) return

    const connection = createRealtimeConnection()
    const refreshFromRealtime = () => {
      refreshCounters()
    }
    const registerCurrentUser = () => {
      connection.invoke("RegisterUser", String(user.ID)).catch(() => null)
    }
    const realtimeEvents = [
      "onMessage",
      "onChatMessage",
      "ReceiveMessage",
      "ReceiveChatMessage",
      "NewMessage",
      "onNotification",
      "onUnreadCountChanged",
      "UnreadCountChanged",
    ]

    realtimeEvents.forEach(eventName => connection.on(eventName, refreshFromRealtime))
    connection.onreconnected(() => {
      registerCurrentUser()
      refreshFromRealtime()
    })
    startConnectionSafe(connection).then(isConnected => {
      if (isConnected) {
        registerCurrentUser()
        refreshCounters()
      }
    })

    return () => {
      realtimeEvents.forEach(eventName => connection.off(eventName, refreshFromRealtime))
      stopConnectionSafe(connection)
    }
  }, [refreshCounters, user.ID])

  const isActiveRoute = useCallback(
    (path: string) => {
      if (path === "/") return location.pathname === "/"
      return location.pathname.startsWith(path)
    },
    [location.pathname]
  )

  const isFeedActive = location.pathname === "/" || location.pathname.startsWith(Routes.Feed.path)
  const isPlacesActive =
    location.pathname.startsWith(Routes.Places.path) ||
    location.pathname.startsWith("/places") ||
    location.pathname.startsWith("/geofences") ||
    location.pathname === Routes.Search.path ||
    location.pathname === Routes.Favorites.path ||
    location.pathname.startsWith("/vehicleDetails")
  const isIncidentsActive = location.pathname.startsWith(Routes.Incidents.path)
  const isRoutesActive =
    location.pathname.startsWith("/routes") ||
    location.pathname.startsWith(Routes.PendingRevisits.path)
  const isReportsActive =
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith(Routes.VehicleVisits.path)
  const isAnalysisActive = location.pathname.startsWith(Routes.Analysis.path)

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token")
    history.push("/login")
  }, [history])

  return (
    <>
      {showChat && (
        <Conversations
          show={showChat}
          toggle={toggleChat}
          onUnreadCountChanged={requestChatCount}
        />
      )}

      <div ref={navRef}>
        <Navbar
          bg="white"
          expand="lg"
          className="responsive-navbar shadow-sm"
          expanded={expanded}
        >
          <div className="container-fluid px-2 px-sm-3">
            <Navbar.Brand
              as={Link}
              to="/"
              className="fw-bold text-primary me-auto me-lg-4 py-1"
              onClick={handleNavClick}
            >
              <OrganizationBox readOnly />
            </Navbar.Brand>

            {/* Mobile Quick Action Buttons (visible only on < lg) */}
            <div className="d-flex align-items-center gap-1 d-lg-none me-1">
              <button
                type="button"
                className="btn btn-light border navbar-mobile-quick-btn"
                onClick={toggleSearch}
                title={t("Search")}
                aria-label={t("Search")}
              >
                <i className="fas fa-search" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="btn btn-light border navbar-mobile-quick-btn position-relative"
                onClick={toggleFavorites}
                title={t("Priority")}
                aria-label={t("Priority")}
              >
                <i className={`fas fa-star ${favoritesCount > 0 ? "text-warning" : "text-secondary"}`} aria-hidden="true" />
                {favoritesCount > 0 && (
                  <span className="navbar-action-badge" aria-live="polite">
                    {favoritesCount > 99 ? "99+" : favoritesCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="btn btn-light border navbar-mobile-quick-btn position-relative"
                onClick={toggleChat}
                title={t("Chats")}
                aria-label={t("Chats")}
              >
                <i className="fas fa-comments text-secondary" aria-hidden="true" />
                {chatCount > 0 && (
                  <span className="navbar-action-badge navbar-action-badge-pulse" aria-live="polite">
                    {chatCount > 99 ? "99+" : chatCount}
                  </span>
                )}
              </button>
            </div>

            <Navbar.Toggle
              aria-controls="responsive-navbar-nav"
              onClick={handleToggle}
              aria-expanded={expanded}
              className="navbar-mobile-hamburger d-lg-none"
              aria-label={t("Toggle navigation", "Menú")}
            >
              <i className={`fas ${expanded ? "fa-times" : "fa-bars"}`} />
            </Navbar.Toggle>

            <Navbar.Offcanvas
              id="responsive-navbar-nav"
              aria-labelledby="responsive-navbar-title"
              placement="end"
              show={expanded}
              onHide={() => setExpanded(false)}
              className="navbar-mobile-drawer"
            >
              <Offcanvas.Header closeButton>
                <Offcanvas.Title id="responsive-navbar-title">
                  <OrganizationBox readOnly />
                </Offcanvas.Title>
              </Offcanvas.Header>

              <Offcanvas.Body className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between flex-grow-1 p-0">
                {/* Mobile User Profile Card (only on mobile) */}
                <div className="navbar-mobile-user-card d-lg-none">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                      style={{ width: 42, height: 42, backgroundColor: "#C12064", flexShrink: 0, fontSize: "1.1rem" }}
                    >
                      {(user.name || user.Name || user.email || "U").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-grow-1">
                      <div className="fw-bold text-dark text-truncate">
                        {user.name || user.Name || "Usuario"}
                      </div>
                      <div className="text-muted small text-truncate">
                        {user.email || user.Email || ""}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center">
                    <span className="badge bg-light text-secondary border">
                      {user.roles || (isSuperAdmin(user) ? "Super Admin" : "User")}
                    </span>
                    <LanguageToggle />
                  </div>
                </div>

                <Nav className="navbar-mobile-nav-list align-items-lg-center flex-lg-row">
                  {/* 1. Inicio (Feed) */}
                  <Nav.Link
                    as={Link}
                    title={t("Inicio", "Inicio")}
                    to={Routes.Feed.path}
                    className={`nav-button d-flex align-items-center ${isFeedActive ? "active" : ""}`}
                    onClick={handleNavClick}
                  >
                    <span className="nav-icon-wrapper d-lg-none">
                      <i className="fas fa-stream text-primary" aria-hidden="true" />
                    </span>
                    <i className="fas fa-stream me-2 text-primary d-none d-lg-inline-block" aria-hidden="true" />
                    <span>{t("Inicio", "Inicio")}</span>
                  </Nav.Link>

                  {/* 2. Lugares (Mapa / Listado) */}
                  <Nav.Link
                    as={Link}
                    title={t("Lugares", "Lugares")}
                    to={Routes.Places.path}
                    className={`nav-button d-flex align-items-center ${isPlacesActive ? "active" : ""}`}
                    onClick={handleNavClick}
                  >
                    <span className="nav-icon-wrapper d-lg-none">
                      <i className="fas fa-map-marker-alt text-success" aria-hidden="true" />
                    </span>
                    <i className="fas fa-map-marker-alt me-2 text-success d-none d-lg-inline-block" aria-hidden="true" />
                    <span>{t("Lugares", "Lugares")}</span>
                  </Nav.Link>

                  {/* 3. Incidencias */}
                  <Nav.Link
                    as={Link}
                    title={t("Incidencias", "Incidencias")}
                    to={Routes.Incidents.path}
                    className={`nav-button d-flex align-items-center ${isIncidentsActive ? "active" : ""}`}
                    onClick={handleNavClick}
                  >
                    <span className="nav-icon-wrapper d-lg-none">
                      <i className="fas fa-exclamation-triangle text-danger" aria-hidden="true" />
                    </span>
                    <i className="fas fa-exclamation-triangle me-2 text-danger d-none d-lg-inline-block" aria-hidden="true" />
                    <span>{t("Incidencias", "Incidencias")}</span>
                  </Nav.Link>

                  {/* 4. Rutas (Desplegable con Ruta Semanal y Re-visitas) */}
                  <NavDropdown
                    title={
                      <span className="d-flex align-items-center">
                        <span className="nav-icon-wrapper d-lg-none">
                          <i className="fas fa-route text-primary" aria-hidden="true" />
                        </span>
                        <i className="fas fa-route me-2 text-primary d-none d-lg-inline-block" aria-hidden="true" />
                        <span>{t("Rutas", "Rutas")}</span>
                      </span>
                    }
                    id="nav-routes-dropdown"
                    className={`nav-dropdown-custom ${isRoutesActive ? "active" : ""}`}
                  >
                    <NavDropdown.Item
                      as={Link}
                      to={Routes.WeeklyRoutes.path}
                      onClick={handleNavClick}
                      className={location.pathname.startsWith(Routes.WeeklyRoutes.path) ? "active" : ""}
                    >
                      <i className="fas fa-calendar-week me-2 text-primary" />
                      {t("Ruta Semanal", "Ruta Semanal")}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to={Routes.PendingRevisits.path}
                      onClick={handleNavClick}
                      className={location.pathname.startsWith(Routes.PendingRevisits.path) ? "active" : ""}
                    >
                      <i className="fas fa-rotate me-2 text-warning" />
                      {t("Re-visitas", "Re-visitas")}
                    </NavDropdown.Item>
                  </NavDropdown>

                  {/* 5. Reportes (Desplegable) */}
                  <NavDropdown
                    title={
                      <span className="d-flex align-items-center">
                        <span className="nav-icon-wrapper d-lg-none">
                          <i className="fas fa-file-lines text-secondary" aria-hidden="true" />
                        </span>
                        <i className="fas fa-file-lines me-2 text-secondary d-none d-lg-inline-block" aria-hidden="true" />
                        <span>{t("Reportes", "Reportes")}</span>
                      </span>
                    }
                    id="nav-reports-dropdown"
                    className={`nav-dropdown-custom ${isReportsActive ? "active" : ""}`}
                  >
                    <NavDropdown.Item
                      as={Link}
                      to={Routes.visitReport.path}
                      onClick={handleNavClick}
                      className={isActiveRoute(Routes.visitReport.path) ? "active" : ""}
                    >
                      <i className="fas fa-clipboard-list me-2 text-muted" />
                      {t("Visits", "Visitas")}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to={Routes.VehicleVisits.path}
                      onClick={handleNavClick}
                      className={isActiveRoute(Routes.VehicleVisits.path) ? "active" : ""}
                    >
                      <i className="fas fa-car me-2 text-muted" />
                      {t("Vehicle Visits", "Visitas en Vehículo")}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to={Routes.agentVisitsReport.path}
                      onClick={handleNavClick}
                      className={isActiveRoute(Routes.agentVisitsReport.path) ? "active" : ""}
                    >
                      <i className="fas fa-walking me-2 text-muted" />
                      {t("Agent Visits", "Visitas de Agentes")}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to="/reports/placesByAgents"
                      onClick={handleNavClick}
                      className={isActiveRoute("/reports/placesByAgents") ? "active" : ""}
                    >
                      <i className="fas fa-map-marked-alt me-2 text-muted" />
                      {t("Places By Agents", "Lugares por Agentes")}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to="/reports/newPlaces"
                      onClick={handleNavClick}
                      className={isActiveRoute("/reports/newPlaces") ? "active" : ""}
                    >
                      <i className="fas fa-plus-circle me-2 text-muted" />
                      {t("New Places", "Nuevos Lugares")}
                    </NavDropdown.Item>
                  </NavDropdown>

                  {/* 6. Análisis */}
                  <Nav.Link
                    as={Link}
                    title={t("Analysis", "Análisis")}
                    to={Routes.Analysis.path}
                    className={`nav-button d-flex align-items-center ${isAnalysisActive ? "active" : ""}`}
                    onClick={handleNavClick}
                  >
                    <span className="nav-icon-wrapper d-lg-none">
                      <i className="fas fa-chart-line text-info" aria-hidden="true" />
                    </span>
                    <i className="fas fa-chart-line me-2 text-info d-none d-lg-inline-block" aria-hidden="true" />
                    <span>{t("Analysis", "Análisis")}</span>
                  </Nav.Link>
                </Nav>

                {/* Mobile Admin Section (only on < lg) */}
                {(user.roles === settings.Roles.Admin || isSuperAdmin(user)) && (
                  <div className="d-lg-none mb-3">
                    <div className="navbar-mobile-section-title">{t("Administración", "Administración")}</div>
                    <div className="d-flex flex-column gap-1">
                      <Link
                        to={Routes.Agents.path}
                        onClick={handleNavClick}
                        className="nav-button d-flex align-items-center"
                      >
                        <span className="nav-icon-wrapper">
                          <i className="fas fa-users text-primary" />
                        </span>
                        <span>{t("Agents", "Agentes")}</span>
                      </Link>
                      <Link
                        to={Routes.Dashboard.path}
                        onClick={handleNavClick}
                        className="nav-button d-flex align-items-center"
                      >
                        <span className="nav-icon-wrapper">
                          <i className="fas fa-tachometer-alt text-secondary" />
                        </span>
                        <span>{t("Dashboard", "Dashboard")}</span>
                      </Link>
                      <Link
                        to={Routes.Settings.path}
                        onClick={handleNavClick}
                        className="nav-button d-flex align-items-center"
                      >
                        <span className="nav-icon-wrapper">
                          <i className="fas fa-user-shield text-info" />
                        </span>
                        <span>{t("Settings", "Ajustes")}</span>
                      </Link>
                      {isSuperAdmin(user) && (
                        <Link
                          to={Routes.Platform.path}
                          onClick={handleNavClick}
                          className="nav-button d-flex align-items-center"
                        >
                          <span className="nav-icon-wrapper">
                            <i className="fas fa-shield-halved text-warning" />
                          </span>
                          <span>{t("Platform Admin", "Platform Admin")}</span>
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {/* Mobile Sign Out Button */}
                <div className="d-lg-none mt-auto pt-3 border-top">
                  <button
                    type="button"
                    className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                    onClick={() => {
                      handleNavClick()
                      handleLogout()
                    }}
                  >
                    <i className="fas fa-sign-out-alt" />
                    <span>{t("Sign Out", "Cerrar sesión")}</span>
                  </button>
                </div>

                {/* Desktop Right Actions (hidden on mobile, visible on desktop) */}
                <div className="d-none d-lg-flex align-items-center navbar-end ms-lg-auto">
                  <button className="btn btn-light border me-2" onClick={toggleSearch} title={t("Search")}>
                    <i className="fas fa-search" aria-hidden="true"></i>
                  </button>
                  <button
                    className="btn btn-light border me-2 position-relative"
                    onClick={toggleFavorites}
                    title={t("Priority")}
                    aria-label={t("Priority")}
                  >
                    <i className={`fas fa-star ${favoritesCount > 0 ? "text-warning" : "text-secondary"}`} aria-hidden="true"></i>
                    {favoritesCount > 0 && (
                      <span className="navbar-action-badge" aria-live="polite">
                        {favoritesCount > 99 ? "99+" : favoritesCount}
                      </span>
                    )}
                  </button>
                  <button
                    className="btn btn-light border me-2 position-relative"
                    onClick={toggleChat}
                    title={t("Chats")}
                    aria-label={t("Chats")}
                  >
                    <i className="fas fa-comments text-secondary" aria-hidden="true"></i>
                    {chatCount > 0 && (
                      <span className="navbar-action-badge navbar-action-badge-pulse" aria-live="polite">
                        {chatCount > 99 ? "99+" : chatCount}
                      </span>
                    )}
                  </button>

                  <Dropdown align="end">
                    <Dropdown.Toggle
                      variant="link"
                      id="dropdown-user"
                      className="nav-link p-2 border-0 bg-transparent hover-bg-light rounded"
                      style={{ boxShadow: "none" }}
                    >
                      <div className="d-flex align-items-center">
                        <i className="fas fa-user-circle fs-4 text-dark me-2" />
                      </div>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="user-dropdown">
                      <Dropdown.Header>
                        <div className="fw-bold text-dark">
                          {user.name || user.Name || "User Name"}
                        </div>
                        <div className="text-muted small">
                          {user.email || user.Email || "user@example.com"}
                        </div>
                        <LanguageToggle />
                      </Dropdown.Header>
                      {(user.roles === settings.Roles.Admin || isSuperAdmin(user)) && (<>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => {
                            history.push(Routes.Agents.path)
                          }}
                          className="d-flex align-items-center"
                        >
                          <i className="fas fa-users me-2 text-primary" />
                          {t("Agents", "Agentes")}
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            history.push(Routes.Dashboard.path)
                          }}
                          className="d-flex align-items-center"
                        >
                          <i className="fas fa-tachometer-alt me-2 text-secondary" />
                          {t("Dashboard", "Dashboard")}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => {
                            history.push(Routes.Settings.path)
                          }}
                          className="d-flex align-items-center"
                        >
                          <i className="fas fa-user-shield me-2" />
                          {t("Settings")}
                        </Dropdown.Item>
                      </>)}
                      {isSuperAdmin(user) && (<>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => {
                            history.push(Routes.Platform.path)
                          }}
                          className="d-flex align-items-center"
                        >
                          <i className="fas fa-shield-halved me-2 text-warning" />
                          {t("Platform Admin")}
                        </Dropdown.Item>
                      </>)}
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={handleLogout}
                        className="d-flex align-items-center"
                      >
                        <i className="fas fa-sign-out-alt me-2 text-danger" />
                        {t("Sign Out")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Offcanvas.Body>
            </Navbar.Offcanvas>
          </div>
        </Navbar>
      </div >
    </>
  )
}
